import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  api,
  getGuestAuth,
  navigate,
  paymentTarget,
  setGuestAuth,
  signInFor,
  useApi,
  type GuestAuth,
} from "./api";
import {
  CopyButton,
  Empty,
  Loading,
  Modal,
  Notice,
  PageTitle,
} from "./common";
import { date, money, plainText } from "./format";
import type { Order } from "./types";

const ORDER_STATUS: Record<string, string> = {
  pending: "待付款",
  paid: "已支付",
  processing: "处理中",
  delivered: "已交付",
  completed: "已完成",
  cancelled: "已取消",
  refunded: "已退款",
};
const FILTERS = [
  { id: "all", label: "全部", statuses: [] },
  { id: "pending", label: "待付款", statuses: ["pending"] },
  { id: "processing", label: "处理中", statuses: ["paid", "processing"] },
  { id: "completed", label: "已完成", statuses: ["delivered", "completed"] },
  { id: "closed", label: "已关闭", statuses: ["cancelled", "refunded"] },
];
interface DeliveryStatus {
  orderNo: string;
  orderStatus: string;
  paid: boolean;
  hasDelivery: boolean;
  shouldContinuePolling: boolean;
  message?: string;
}
function orderPath(orderNo: string) {
  return "/orders/" + encodeURIComponent(orderNo);
}

function StatusBadge({ status }: { status: string }) {
  const text = ORDER_STATUS[status] || "状态待确认";
  return <span className={"badge " + status}>{text}</span>;
}

function ResumePayment({ orderNo }: { orderNo: string }) {
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState("");
  const [error, setError] = useState("");
  const pending = useRef(false);

  async function resume() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await api<{ payUrl: string }>(
        "/epay/resume/" + encodeURIComponent(orderNo),
        {},
      );
      setTarget(paymentTarget(result.payUrl));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {target ? (
        <a className="button primary small" href={target}>
          打开支付页面 ↗
        </a>
      ) : (
        <button
          className="button primary small"
          disabled={busy}
          onClick={resume}
        >
          {busy ? "正在获取支付链接…" : "继续支付"}
        </button>
      )}
      <Notice>{error}</Notice>
    </div>
  );
}

function OrderDetails({
  orderNo,
  paymentReturn,
  onSwitchGuest,
}: {
  orderNo: string;
  paymentReturn: boolean;
  onSwitchGuest?: () => void;
}) {
  const [version, setVersion] = useState(0);
  const [pollVersion, setPollVersion] = useState(0);
  const [poll, setPoll] = useState<DeliveryStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [pollError, setPollError] = useState("");
  const [limitReached, setLimitReached] = useState(false);
  const {
    data: order,
    loading,
    error,
  } = useApi<Order>(orderPath(orderNo), version);

  useEffect(() => {
    setPoll(null);
    setPollError("");
    setAttempts(0);
    setLimitReached(false);
    if (!paymentReturn) {
      setPolling(false);
      return;
    }
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let count = 0;
    let previousStatus = "";
    setPolling(true);

    async function check() {
      if (controller.signal.aborted) return;
      count += 1;
      setAttempts(count);
      try {
        const requestSignal = AbortSignal.any([
          controller.signal,
          AbortSignal.timeout(25000),
        ]);
        const status = await api<DeliveryStatus>(
          orderPath(orderNo) + "/delivery-status",
          undefined,
          requestSignal,
        );
        if (controller.signal.aborted) return;
        setPoll(status);
        if (previousStatus !== status.orderStatus || status.hasDelivery)
          setVersion((n) => n + 1);
        previousStatus = status.orderStatus;
        if (status.hasDelivery || !status.shouldContinuePolling) {
          setPolling(false);
          return;
        }
        if (count >= 12) {
          setPolling(false);
          setLimitReached(true);
          return;
        }
        timer = setTimeout(() => void check(), 5000);
      } catch (e) {
        if (!controller.signal.aborted) {
          setPollError((e as Error).message);
          setPolling(false);
        }
      }
    }
    void check();
    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [orderNo, paymentReturn, pollVersion]);

  function refresh() {
    setVersion((n) => n + 1);
    if (paymentReturn) setPollVersion((n) => n + 1);
  }

  return (
    <div className="stack">
      <PageTitle
        title={paymentReturn ? "支付结果" : "订单详情"}
        subtitle="订单状态与交付内容以系统实际记录为准。"
        action={
          <div className="grid-2">
            <button
              className="button subtle"
              onClick={() => navigate("/transaction-record")}
            >
              全部订单
            </button>
            <button
              className="button subtle"
              onClick={refresh}
              disabled={loading || polling}
            >
              刷新状态
            </button>
          </div>
        }
      />
      {paymentReturn && (
        <section className="panel stack" aria-label="支付状态">
          <strong>
            {poll?.hasDelivery
              ? "交付内容已就绪"
              : poll?.paid
                ? "已收到支付确认"
                : poll && !poll.shouldContinuePolling
                  ? "订单状态：" + (ORDER_STATUS[poll.orderStatus] || "待确认")
                  : polling
                    ? "正在核对订单状态"
                    : "支付状态尚未确认"}
          </strong>
          {poll?.message && <p role="status">{poll.message}</p>}
          {polling && (
            <p className="muted" role="status">
              正在检查支付与交付状态（{attempts}/12）…
            </p>
          )}
          <Notice>{pollError}</Notice>
          {limitReached && (
            <Notice kind="info">
              本次自动检查已结束，订单状态仍未完成。稍后可刷新查看，请先核对现有订单再决定是否付款。
            </Notice>
          )}
          {!polling &&
            !pollError &&
            poll &&
            !poll.hasDelivery &&
            !poll.shouldContinuePolling && (
              <p>
                当前订单状态：{ORDER_STATUS[poll.orderStatus] || "待确认"}。
              </p>
            )}
        </section>
      )}
      {loading && <Loading />}
      <Notice>{error}</Notice>
      {error && onSwitchGuest && (
        <button className="button subtle" onClick={onSwitchGuest}>
          重新输入查询邮箱和查询密码
        </button>
      )}
      {order && (
        <>
          <section className="panel stack" aria-label="订单信息">
            <div className="order-row">
              <h2>{order.planName || "订单商品"}</h2>
              <StatusBadge status={order.status} />
            </div>
            <dl className="details-grid">
              <div>
                <dt>订单号</dt>
                <dd style={{ overflowWrap: "anywhere" }}>
                  {order.orderNo}{" "}
                  <CopyButton value={order.orderNo} label="复制订单号" />
                </dd>
              </div>
              <div>
                <dt>订单金额</dt>
                <dd>{money(order.displayAmount)}</dd>
              </div>
              {order.invoiceRequested && <div>
                <dt>发票</dt>
                <dd>需要开发票 · 已含服务费 {money(order.invoiceFeeDisplayAmount)}</dd>
              </div>}
              <div>
                <dt>下单时间</dt>
                <dd>{date(order.createdAt)}</dd>
              </div>
              <div>
                <dt>支付时间</dt>
                <dd>{date(order.paidAt)}</dd>
              </div>
            </dl>
            {order.status === "pending" && !polling && (
              <ResumePayment key={order.orderNo} orderNo={order.orderNo} />
            )}
          </section>
          <section className="panel stack" aria-label="交付内容">
            <div className="order-row">
              <h2>交付内容</h2>
              {order.delivery?.cardKey && (
                <CopyButton
                  value={order.delivery.cardKey}
                  label="复制交付内容"
                />
              )}
            </div>
            {order.delivery?.cardKey ? (
              <pre
                className="delivery-content"
                style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {order.delivery.cardKey}
              </pre>
            ) : (
              <p className="muted">
                {order.status === "pending"
                  ? "支付完成后可在这里查看交付内容。"
                  : ["paid", "processing"].includes(order.status)
                    ? "付款已确认，交付内容暂未就绪，请稍后刷新。"
                    : "此订单暂无可显示的交付内容。"}
              </p>
            )}
            {order.deliveryInstructions && (
              <div className="stack">
                <h3>使用说明</h3>
                <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {plainText(order.deliveryInstructions)}
                </p>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function OrderList({
  guestEmail,
  onSwitchGuest,
}: {
  guestEmail?: string;
  onSwitchGuest?: () => void;
}) {
  const { data, loading, error, reload } = useApi<Order[]>("/orders");
  const [filter, setFilter] = useState("all");
  const orders = Array.isArray(data) ? data : [];
  const activeFilter = FILTERS.find((item) => item.id === filter) || FILTERS[0];
  const visible = orders.filter(
    (order) =>
      !activeFilter.statuses.length ||
      activeFilter.statuses.includes(order.status),
  );

  return (
    <div className="stack">
      <PageTitle
        title="我的订单"
        subtitle={guestEmail
          ? `查询邮箱 ${guestEmail} 名下的订单与交付内容。`
          : "购买记录与交付内容，都在这里。"}
        action={
          <div className="actions">
            {onSwitchGuest && (
              <button className="button subtle" onClick={onSwitchGuest}>
                换个邮箱查询
              </button>
            )}
            <button className="button subtle" disabled={loading} onClick={reload}>
              刷新订单
            </button>
          </div>
        }
      />
      <div className="order-row" role="group" aria-label="订单筛选">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            className={
              "button small " + (filter === item.id ? "primary" : "subtle")
            }
            aria-pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      {loading && <Loading />}
      <Notice>{error}</Notice>
      {!loading && !error && !orders.length && (
        <Empty
          title={onSwitchGuest ? "没有找到订单" : "还没有订单"}
          text={onSwitchGuest
            ? "请确认查询邮箱与查询密码和下单时填写的一致。"
            : "挑选合适的订阅服务，购买后可在这里查看记录。"}
          action={
            <div className="actions">
              {onSwitchGuest && (
                <button className="button subtle" onClick={onSwitchGuest}>
                  重新输入
                </button>
              )}
              <button
                className="button primary"
                onClick={() => navigate("/subscribe")}
              >
                选购服务
              </button>
            </div>
          }
        />
      )}
      {!loading && !error && orders.length > 0 && !visible.length && (
        <Empty title="此分类暂无订单" text="可以切换到其他订单状态查看。" />
      )}
      <div className="order-list">
        {visible.map((order) => (
          <article className="panel stack" key={order.orderNo}>
            <div className="order-row">
              <span className="muted" style={{ overflowWrap: "anywhere" }}>
                {order.orderNo}
              </span>
              <StatusBadge status={order.status} />
            </div>
            <div className="order-row">
              <div>
                <h2>{order.planName || "订单商品"}</h2>
                <p className="muted">
                  {date(order.createdAt)} ·{" "}
                  {order.type === "ready-account" ? "成品账号" : "订阅服务"}
                  {order.invoiceRequested && " · 需要开发票"}
                </p>
              </div>
              <strong>{money(order.displayAmount)}</strong>
            </div>
            <div className="order-row">
              <button
                className="button subtle small"
                onClick={() => navigate(orderPath(order.orderNo))}
              >
                查看详情
              </button>
              {order.status === "pending" && (
                <ResumePayment orderNo={order.orderNo} />
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function GuestLookup({
  paymentReturn,
  onSubmit,
}: {
  paymentReturn: boolean;
  onSubmit: (auth: GuestAuth) => void;
}) {
  const [email, setEmail] = useState(""),
    [password, setPassword] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    if (email.trim() && password.trim())
      onSubmit({ email: email.trim(), password: password.trim() });
  }
  return (
    <div className="stack">
      <PageTitle
        title={paymentReturn ? "支付结果" : "订单查询"}
        subtitle="输入下单时填写的查询邮箱和查询密码，查看订单状态与交付内容。"
      />
      <form className="panel stack" onSubmit={submit} aria-label="订单查询">
        <label>
          查询邮箱
          <input
            type="email"
            name="guestEmail"
            autoComplete="email"
            required
            maxLength={320}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>
        <label>
          查询密码
          <input
            type="text"
            name="guestPassword"
            autoComplete="off"
            required
            maxLength={256}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="下单时自定义的查询密码"
          />
        </label>
        <button
          className="button primary full"
          disabled={!email.trim() || !password.trim()}
        >
          查询订单
        </button>
        <p className="fine-print">已有账号的用户可以登录后查看全部订单。</p>
        <button
          type="button"
          className="button subtle small"
          onClick={() => signInFor(location.pathname + location.search)}
        >
          登录 / 注册
        </button>
      </form>
    </div>
  );
}

export default function Orders({
  orderNo,
  paymentReturn = false,
  loggedIn = false,
}: {
  orderNo?: string;
  paymentReturn?: boolean;
  loggedIn?: boolean;
}) {
  // 游客（2026-09-26）：没登录就凭查询邮箱 + 查询密码查单，凭证只留在本标签页会话
  const [guest, setGuest] = useState<GuestAuth | null>(() => getGuestAuth());
  function switchGuest() {
    setGuestAuth(null);
    setGuest(null);
  }
  if (paymentReturn && !orderNo)
    return (
      <Empty
        title="没有找到订单号"
        text="可从我的订单查看最新支付状态。"
        action={
          <button
            className="button primary"
            onClick={() => navigate("/transaction-record")}
          >
            前往我的订单
          </button>
        }
      />
    );
  if (!loggedIn && !guest)
    return (
      <GuestLookup
        paymentReturn={paymentReturn}
        onSubmit={(auth) => {
          setGuestAuth(auth);
          setGuest(auth);
        }}
      />
    );
  const guestKey = !loggedIn && guest ? guest.email : "";
  return orderNo ? (
    <OrderDetails
      key={orderNo + guestKey}
      orderNo={orderNo}
      paymentReturn={paymentReturn}
      onSwitchGuest={loggedIn ? undefined : switchGuest}
    />
  ) : (
    <OrderList
      key={guestKey}
      guestEmail={guestKey || undefined}
      onSwitchGuest={loggedIn ? undefined : switchGuest}
    />
  );
}
